import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ShieldAlert, RefreshCw, Radar, Plus } from "lucide-react";
import AppShell from "@/components/AppShell";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CreateIncidentDialog } from "@/components/incidents/CreateIncidentDialog";
import { IncidentDetail } from "@/components/incidents/IncidentDetail";

const SEV_COLOR = { Critical: "bg-red-100 text-red-700", High: "bg-orange-100 text-orange-700", Medium: "bg-amber-100 text-amber-700", Low: "bg-blue-100 text-blue-700", Informational: "bg-slate-100 text-slate-600" };
const CONTAIN_ROLES = ["super_admin", "security_admin"];

export default function IncidentResponse() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [selected, setSelected] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const canContain = CONTAIN_ROLES.includes(user?.role);

  const load = useCallback(async () => {
    try {
      const [s, l] = await Promise.all([
        api.get("/admin/security/incidents/dashboard/summary"),
        api.get("/admin/security/incidents", { params: { limit: 100 } }),
      ]);
      setSummary(s.data); setIncidents(l.data.incidents);
      setSelected((prev) => prev ? l.data.incidents.find((x) => x.incident_id === prev.incident_id) || null : null);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => { try { const { data } = await api.get(`/admin/security/incidents/${id}`); setSelected(data); } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); } };
  const refreshSelected = async () => { if (selected) await openDetail(selected.incident_id); await load(); };
  const runDetect = async () => { if (busy) return; setBusy(true); try { const { data } = await api.post("/admin/security/incidents/detect", { window_hours: 24, create_suspected: true }); toast.success(`Detection: ${data.triggered.length} signal(s) triggered${data.created_suspected_incident ? " · suspected incident opened" : ""}`); load(); } catch (e) { const d = e?.response?.data?.detail; toast.error(d?.error === "mfa_required" ? "MFA-verified session required." : formatApiError(d)); } finally { setBusy(false); } };

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto p-6 space-y-5" data-testid="incident-response">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-heading font-bold text-[#0A0F1C] flex items-center gap-2"><ShieldAlert size={22} className="text-red-600" /> Incident Response & Recovery</h1>
            <p className="text-sm text-slate-500">Preview-only · human-controlled · AI advisory/draft only · no external notifications are ever sent from here · no Production actions.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button data-testid="incident-detect" disabled={busy} onClick={runDetect} className="h-9 px-3 rounded-md border border-slate-200 text-sm flex items-center gap-1.5 disabled:opacity-40"><Radar size={14} /> Run detection</button>
            <button data-testid="incident-refresh" onClick={load} className="h-9 px-3 rounded-md border border-slate-200 text-sm flex items-center gap-1.5"><RefreshCw size={14} /> Refresh</button>
            <button data-testid="incident-new" onClick={() => setCreateOpen(true)} className="h-9 px-3 rounded-md bg-red-600 text-white text-sm flex items-center gap-1.5"><Plus size={14} /> Open incident</button>
          </div>
        </div>

        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="incident-summary">
            <div className="rounded-lg border border-slate-200 bg-white p-3"><div className="text-[10px] uppercase text-slate-400">Open incidents</div><div className="text-xl font-semibold text-[#0A0F1C]">{summary.open_incidents}</div></div>
            <div className="rounded-lg border border-slate-200 bg-white p-3"><div className="text-[10px] uppercase text-slate-400">Total</div><div className="text-xl font-semibold text-[#0A0F1C]">{summary.total}</div></div>
            <div className="rounded-lg border border-slate-200 bg-white p-3"><div className="text-[10px] uppercase text-slate-400">Critical</div><div className="text-xl font-semibold text-red-600">{summary.by_severity?.Critical || 0}</div></div>
            <div className="rounded-lg border border-slate-200 bg-white p-3"><div className="text-[10px] uppercase text-slate-400">Overdue actions</div><div className="text-xl font-semibold text-orange-600">{(summary.overdue_actions || []).length}</div></div>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-4">
          <div className="border border-slate-200 rounded-lg overflow-x-auto">
            <table className="w-full text-sm min-w-[360px]" data-testid="incident-table">
              <thead className="bg-slate-50 text-slate-500 text-xs"><tr><th className="px-3 py-2 text-left">Incident</th><th className="px-3 py-2 text-left">Severity</th><th className="px-3 py-2 text-left">Status</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {incidents.length === 0 ? <tr><td colSpan={3} className="px-3 py-8 text-center text-slate-400">No incidents. Open one or run detection.</td></tr>
                  : incidents.map((i) => (
                    <tr key={i.incident_id} data-testid={`incident-row-${i.incident_id}`} onClick={() => openDetail(i.incident_id)} className={`cursor-pointer hover:bg-slate-50 ${selected?.incident_id === i.incident_id ? "bg-emerald-50" : ""}`}>
                      <td className="px-3 py-2"><div className="font-medium text-[#0A0F1C]">{i.title}</div><div className="text-xs text-slate-400">{i.incident_type?.replace(/_/g, " ")}</div></td>
                      <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full text-[10px] ${SEV_COLOR[i.severity] || "bg-slate-100"}`}>{i.severity}</span></td>
                      <td className="px-3 py-2 text-xs text-slate-500">{i.status}</td>
                    </tr>))}
              </tbody>
            </table>
          </div>
          <div>
            {selected ? <IncidentDetail incident={selected} canContain={canContain} onChanged={refreshSelected} />
              : <div className="rounded-lg border border-dashed border-slate-200 p-8 text-center text-sm text-slate-400" data-testid="incident-detail-empty">Select an incident to view and manage it.</div>}
          </div>
        </div>
      </div>
      <CreateIncidentDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={(inc) => { load(); setSelected(inc); }} />
    </AppShell>
  );
}
